import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  ChatCircleDots,
  CheckCircle,
  Flask,
  Gauge,
  PencilSimple,
  UploadSimple,
  Users,
  Waveform,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const guideSteps = [
  {
    icon: UploadSimple,
    title: "Upload your first call",
    copy: "Open New Call, add the recording, call title, and customer or company name. Calls can be up to the beta limits shown below.",
    path: "/upload",
    action: "Open New Call",
  },
  {
    icon: Waveform,
    title: "Review the full dissection",
    copy: "Use Findings for evidence-backed signals, Coaching for the next move, and CRM Notes for a ready-to-copy record. CRM Notes are generated in English by default.",
    path: "/dashboard",
    action: "View dashboard",
  },
  {
    icon: Users,
    title: "Keep customer history connected",
    copy: "Reuse the same customer or company name on future uploads. Callsect will extend the relationship timeline automatically.",
    path: "/customers",
    action: "View customers",
  },
  {
    icon: PencilSimple,
    title: "Teach Callsect your writing style",
    copy: "Add real CRM notes to Writing DNA so future notes better match your wording, structure, and level of detail.",
    path: "/profile",
    action: "Open Writing DNA",
  },
  {
    icon: Flask,
    title: "Use the Sandbox carefully",
    copy: "Test transcription and generation separately without creating a saved customer call. Sandbox use has its own beta allowance.",
    path: "/sandbox",
    action: "Open Sandbox",
  },
  {
    icon: ChatCircleDots,
    title: "Send feedback",
    copy: "Use Share feedback in the sidebar to report bugs, suggest improvements, or describe anything that feels unclear.",
  },
];

function valueOrUnlimited(value) {
  return value === null || value === undefined ? "Unlimited" : value;
}

function allowance(used, limit, remaining) {
  if (limit === null || limit === undefined) return "Owner exemption — no beta quota";
  return `${used ?? 0} of ${limit} used · ${remaining ?? 0} remaining`;
}

export default function BetaQuickStartDialog({ open, onClose, persistOnClose = false }) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [limits, setLimits] = useState(null);
  const [loadingLimits, setLoadingLimits] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoadingLimits(true);
    api.get("/beta/limits")
      .then(({ data }) => active && setLimits(data))
      .catch(() => active && setLimits(null))
      .finally(() => active && setLoadingLimits(false));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      active = false;
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const finish = async (path = null) => {
    if (closing) return;
    setClosing(true);
    try {
      if (persistOnClose) {
        await api.post("/account/onboarding-seen");
        await refreshUser();
      }
      onClose();
      if (path) navigate(path);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save the quick-start guide status.");
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="beta-guide" role="dialog" aria-modal="true" aria-labelledby="beta-guide-title">
      <div className="beta-guide__backdrop" />
      <section className="beta-guide__panel">
        <header className="beta-guide__header">
          <span className="beta-guide__header-icon" aria-hidden="true">
            <BookOpen size={24} weight="duotone" />
          </span>
          <div>
            <div className="brand-eyebrow">Private beta quick start</div>
            <h2 id="beta-guide-title">Welcome to Callsect</h2>
            <p>A brief guide to your first test and the allowances attached to your beta account.</p>
          </div>
          {!persistOnClose && (
            <button type="button" className="beta-guide__close" onClick={() => onClose()} aria-label="Close guide">
              <X size={19} />
            </button>
          )}
        </header>

        <div className="beta-guide__body">
          <div className="beta-guide__steps">
            {guideSteps.map(({ icon: Icon, title, copy, path, action }, index) => (
              <article className="beta-guide__step" key={title}>
                <span className="beta-guide__step-number">{index + 1}</span>
                <span className="beta-guide__step-icon" aria-hidden="true"><Icon size={19} weight="duotone" /></span>
                <div>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                  {path && (
                    <button type="button" onClick={() => finish(path)} disabled={closing}>
                      {action}<ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>

          <aside className="beta-guide__limits">
            <div className="beta-guide__limits-heading">
              <span aria-hidden="true"><Gauge size={21} weight="duotone" /></span>
              <div>
                <div className="account-section-label">Your current allowances</div>
                <h3>Private-beta limits</h3>
              </div>
            </div>

            {loadingLimits ? (
              <p className="beta-guide__limits-loading">Loading your current limits…</p>
            ) : limits ? (
              <div className="beta-guide__limit-grid">
                <LimitCard label="Saved call uploads" value={allowance(limits.uploaded_calls_used, limits.uploaded_calls_limit, limits.uploaded_calls_remaining)} />
                <LimitCard label="Maximum recording" value={`${valueOrUnlimited(limits.max_audio_minutes)} minutes`} />
                <LimitCard label="Maximum file size" value={`${valueOrUnlimited(limits.max_upload_mb)} MB`} />
                <LimitCard label="Regenerations" value={limits.generations_per_call == null ? "Owner exemption" : `Up to ${limits.generations_per_call} generations per call output`} />
                <LimitCard label="Sandbox transcriptions" value={allowance(limits.sandbox_transcriptions_used, limits.sandbox_transcriptions_limit, limits.sandbox_transcriptions_remaining)} />
                <LimitCard label="Sandbox generations" value={allowance(limits.sandbox_generations_used, limits.sandbox_generations_limit, limits.sandbox_generations_remaining)} />
              </div>
            ) : (
              <p className="beta-guide__limits-loading">Your live allowance counters could not be loaded. They remain visible on New Call.</p>
            )}

            <p className="beta-guide__limits-note">
              These are Callsect private-beta allowance counters, not OpenAI charges or a bill to you.
            </p>
          </aside>
        </div>

        <footer className="beta-guide__actions">
          {!persistOnClose && (
            <button type="button" className="beta-guide__secondary" onClick={onClose} disabled={closing}>
              Close
            </button>
          )}
          <button type="button" className="beta-guide__primary" onClick={() => finish("/upload")} disabled={closing}>
            <CheckCircle size={18} weight="fill" />
            {closing ? "Saving…" : persistOnClose ? "Got it — start testing" : "Open New Call"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function LimitCard({ label, value }) {
  return (
    <article className="beta-guide__limit-card">
      <small>{label}</small>
      <strong>{value}</strong>
    </article>
  );
}
